<script lang="ts">
  import * as _ from "lodash";
  import type { Order } from "@shared/types/Order";
  import type { PaginationDetails } from "@shared/types/Pagination";
  import { convertToLocalWithTime } from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import TableSortControl from "@shared/svelte/Components/TableSortControl.svelte";
  import SelectFilter from "@shared/svelte/Components/SelectFilter.svelte";
  import SmallPagination from "@shared/svelte/Components/Pagination/InlineSmall.svelte";
  import { get as getStoreValue } from "svelte/store";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  export let orders: Order[];
  export let orderState: string;
  export let orderSearch: string | string[] | number;
  export let fromDate: string;
  export let toDate: string;
  export let repFilter: string;
  export let ordersPagination: any;

  export let orderStates: Array<{ [key: string]: string }>;
  export let managedChoices: Array<{ [key: string]: string }>;

  export let callbacks: any = {};
  export let direction: string;
  export let order: string;

  const pagination: PaginationDetails = {
    page: ordersPagination.page,
    perPage: ordersPagination.perPage,
    totalEntries: ordersPagination.totalEntries,
    goToPage: ordersPagination.goToPage,
  };

  $: unseen = function (order: Order) {
    return order.state == "pending" && !order.seen_by_rep_at;
  };

  $: emptyState = function () {
    let noResults = "There are no orders available to review.",
      noSearchResults =
        "No orders match your search criteria, please change your criteria to view orders.";
    return orderSearch ? noSearchResults : noResults;
  };

  const areAllDistributorsNonIntegrated = _.every(
    getStoreValue(CurrentUserStore)["distributors"],
    distributor => !distributor.integrated,
  );

  const orderStatus = {
    pending: {
      status: "Open",
      class: "fizz-pill-grey",
    },
    accepted: {
      status: areAllDistributorsNonIntegrated ? "Confirmed" : "Accepted",
      class: "fizz-pill-green",
    },
    rejected: {
      status: "Rejected",
      class: "fizz-pill-red",
    },
    pending_rejection: {
      status: "Rejected",
      class: "fizz-pill-red",
    },
  };

  function getItemCount(order: Order) {
    let itemCounts = [
      order["box_count"],
      order["case_count"],
      order["keg_count"],
      order["unit_count"],
    ].filter(type => type != null);

    return itemCounts.reduce((prev, current) => prev + current, 0);
  }

  function getItemTypeCount(order: Order) {
    let productsInOrder = [];
    let productTypes = ["box", "case", "keg", "unit"];

    productTypes.forEach(type => {
      let attr = type + "_count";
      if (order[attr] > 0) {
        productsInOrder.push(pluralize(type, order[attr], true));
      }
    });

    return productsInOrder.join(", ");
  }

  const runFilter = function () {
    callbacks.runSortAndFilter({
      orderState: orderState,
      orderSearch: orderSearch,
      orderPage: 1,
      fromDate: fromDate,
      toDate: toDate,
      repFilter: repFilter,
    });
  };

  const runSearch = _.debounce(runFilter, 250);

  const runSortCallback = function (sort: { [key: string]: string }) {
    callbacks.sortCallBack({
      order: sort.order,
      direction: sort.direction,
    });
  };

  $: sortObj = {
    type: order,
    direction: direction,
    sortCallBack: runSortCallback,
  };
</script>

<style lang="scss">
  tr.unseen-order {
    font-weight: 600;
  }

  .unseen-order .seen-status-marker {
    position: relative;
    right: 0.9rem;
    width: 8px;
    height: 8px;
    background: #1f9cc6;
    border: 1px solid #1f9cc6;
    border-radius: 20px;
    filter: blur(2px);
  }

  .unseen-order .status-label {
    position: relative;
    right: 24px;
  }

  .fizz-pill-grey {
    background-color: #e8e8e8;
  }

  .table-container {
    overflow-y: auto;
  }

  .capitalize {
    text-transform: capitalize;
  }

  .table {
    border-collapse: separate;
  }

  .sticky-header th {
    position: sticky;
    top: 0px;
    z-index: 10;
    background-color: #ffffff;
  }

  .sticky-element {
    border-bottom: 1px solid #eaeaea;

    @media (max-height: 812px) {
      border-bottom: unset;
    }
  }

  .fizz-styles .fizz-table tr:first-of-type th {
    border-top: unset;
  }

  .overflow {
    max-height: calc(100vh - 275px);
    overflow-y: auto;
  }
  .pagination-wrapper {
    padding-right: 1.5rem;

    @media (max-height: 812px) {
      padding-left: 1.5rem;
      margin-bottom: 0.5rem;
    }
  }
</style>

<div class="fizz-styles" style="padding: 15px 20px;">
  <div class="fizz-flex-row-responsive fizz-surface fizz-sticky-top sticky-element">
    <div
      class="fizz-flex-row-8-responsive fizz-stack-8 fizz-inset-square-16 fizz-flex-item"
      style="margin-bottom: 0"
    >
      <input
        type="text"
        id="orders-search"
        name="orders_search"
        class="fizz-input-search fizz-full-width"
        style="--max-width: 40%"
        placeholder="Search by retailer or order number"
        bind:value={orderSearch}
        on:keyup={runSearch}
      />

      <SelectFilter
        fontColor={"rgb(0, 43, 72)"}
        selectId={"orderState"}
        options={orderStates}
        bind:selectedValue={orderState}
        onChange={runFilter}
      ></SelectFilter>

      {#if callbacks.isManager()}
        <SelectFilter
          fontColor={"rgb(0, 43, 72)"}
          selectId={"managedChoices"}
          options={managedChoices}
          bind:selectedValue={repFilter}
          onChange={runFilter}
        ></SelectFilter>
      {/if}
      <DatePicker
        bind:startDate={fromDate}
        bind:endDate={toDate}
        defaultButtonLabel={"All Order Dates"}
        runFilters={runFilter}
        minWidth={"275px"}
      ></DatePicker>
    </div>
    <div class="pagination-wrapper">
      {#if orders && orders.length > 0 && ordersPagination.perPage < ordersPagination.totalEntries}
        <SmallPagination {pagination} />
      {/if}
    </div>
  </div>

  <div class="overflow">
    <table class="fizz-table fizz-stack-24">
      <!-- table header -->
      <thead class="sticky-header">
        <tr>
          <th width="10%">Status</th>
          <th width="20%">
            <div class="fizz-flex-row">
              Order Date
              <TableSortControl sortFilter={sortObj} colName={"order_date"} />
            </div>
          </th>
          <th width="25%">Retailer</th>
          <th width="15%">Order Number</th>
          <th width="20%">Items</th>
          <th width="10%" class="fizz-align-right">
            <div class="fizz-flex-row fizz-justify-end">
              Est. Total
              <TableSortControl sortFilter={sortObj} colName={"order_total"} />
            </div>
          </th>
        </tr>
      </thead>

      <!-- table body -->
      <tbody>
        {#if orders.length > 0}
          {#each orders as order}
            <tr
              id="order-{order.id}"
              class="cursor-pointer {unseen(order) ? 'unseen-order' : 'seen-order'}"
              on:click={() => callbacks.viewOrder(order.id)}
            >
              <td data-label="Status">
                <div class="fizz-flex-row">
                  {#if unseen(order)}
                    <div class="seen-status-marker"></div>
                  {/if}
                  <span class="{orderStatus[order.state].class} status-label">
                    {orderStatus[order.state].status}
                  </span>
                </div>
              </td>
              <td data-label="Order Date">{convertToLocalWithTime(order.submitted_at)}</td>
              <td data-label="Retailer" class="fizz-flex-row">
                <p>
                  {order.retailer.name}
                  <br />
                  <span>
                    {order.submitted_by_user.first_name}
                    {order.submitted_by_user.last_name}
                  </span>
                </p>
              </td>
              <td data-label="Order Number">#{order.id}</td>
              <td data-label="Items">
                <strong>{getItemCount(order)} Items &nbsp</strong>
                <p class="capitalize">{getItemTypeCount(order)}</p>
              </td>
              <td data-label="Est. Total" class="fizz-text-center">
                <FormatMoney input={order.total} formatType={"5"}></FormatMoney>
              </td>
            </tr>
          {/each}
        {:else}
          <tr class="fizz-text-center">
            <td class="layout-1" colspan={8} style="padding-top: 8vh;">
              <strong class="padding--small"> No Orders </strong>
              <div class="padding--smaller">
                {emptyState()}
              </div>
            </td>
          </tr>
        {/if}
      </tbody>
    </table>
  </div>
</div>
